import { CustomError } from '@mortgage-pos/types'
import { DEFAULT_FEATURE_FLAGS, ffPayload } from '../featureFlagTypes'

export type FeatureFlagValues = Record<string, ffPayload>

const RETRY_DELAY_MS = 500

async function fetchOnce(apiBaseUrl: string): Promise<FeatureFlagValues> {
  const response = await fetch(`${apiBaseUrl}/api/feature-flags`, {
    method: 'GET',
    // Consistent with the rest of the app; endpoint is unauthenticated today.
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })

  if (!response.ok) {
    throw new CustomError(
      'FeatureFlagsHttpError',
      `GET /api/feature-flags failed with status ${response.status}`,
      response.status,
      null,
      // Only server errors are worth retrying; 4xx will fail the same way.
      response.status >= 500
    )
  }

  let data: FeatureFlagValues
  try {
    data = (await response.json()) as FeatureFlagValues
  } catch (parseError) {
    // Body-stream transport failures stay network-classified.
    if (!(parseError instanceof SyntaxError)) throw parseError

    // Malformed JSON on a completed 2xx is a server problem — keep the status.
    throw new CustomError(
      'FeatureFlagsParseError',
      `GET /api/feature-flags returned an unparseable body with status ${response.status}`,
      response.status,
      null,
      true
    )
  }

  return data ?? {}
}

/**
 * Fetches flag map from `GET /api/feature-flags`, retrying once on network
 * failures and 5xx responses. Throws so callers can decide how to log / fall
 * back:
 * - `CustomError` with `statusCode` when the server responded non-2xx or
 *   returned an unparseable body.
 * - The underlying `TypeError` when the fetch never completed (network drop,
 *   page unload, blocker).
 * Callers should merge the result on top of `DEFAULT_FEATURE_FLAGS`.
 */
export async function fetchClientFeatureFlags(
  apiBaseUrl: string
): Promise<FeatureFlagValues> {
  try {
    return await fetchOnce(apiBaseUrl)
  } catch (error) {
    // Network errors (fetch rejection) and 5xx are transient — retry once.
    if (error instanceof CustomError && !error.retryable) throw error

    await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS))

    try {
      return await fetchOnce(apiBaseUrl)
    } catch (retryError) {
      // Prefer the status-carrying failure, freshest first.
      if (retryError instanceof CustomError) throw retryError
      if (error instanceof CustomError) throw error
      throw retryError
    }
  }
}

/**
 * Merges a partial map over `DEFAULT_FEATURE_FLAGS` so any flag missing from
 * the server response falls back to its hardcoded default.
 */
export function mergeWithDefaults(
  values: FeatureFlagValues
): FeatureFlagValues {
  return { ...DEFAULT_FEATURE_FLAGS, ...values }
}
